<template>
  <account-layout>
    <el-form :model="form" :rules="signUpRules" ref="form">
      <el-row :gutter="60">
        <el-col :span="24">
          <div class="su-title">Complete Your Profile</div>
        </el-col>
        <el-col :span="24" class="row-space">
          <div>First Name<span class="required"></span></div>
          <el-form-item prop="firstName">
            <el-input class="input-space" v-model="form.firstName" placeholder="Type here..." disabled />
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <div>Last Name<span class="required"></span></div>
          <el-form-item prop="lastName">
            <el-input class="input-space" v-model="form.lastName" placeholder="Type here..." disabled />
          </el-form-item>
        </el-col>
        <el-col :span="24" class="row-space">
          <div>Email<span class="required"></span></div>
          <el-form-item prop="email">
            <el-input class="input-space" type="email" v-model="form.email" placeholder="Type here..." disabled />
          </el-form-item>
        </el-col>
        <el-col :span="24" v-if="showPasswordField">
          <div>Password<span class="required">*</span></div>
          <el-form-item prop="password">
            <el-input class="input-space" type="password" v-model="form.password" placeholder="Type here..." show-password/>
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <div>Mobile<span class="required">*</span></div>
          <el-form-item prop="mobile">
             <vue-tel-input
             autoFormat
             autoDefaultCountry
             :defaultCountry="countryCode"
             dropdownOptions.showDialCodeInSelection="false"
             @open="openFlagDropdown = true"
            @close="openFlagDropdown = false"
            @country-changed="handleCountryChanged"
             v-model="form.mobile">
              <template v-slot:arrow-icon>
                <span><i v-if="openFlagDropdown" class="el-icon-arrow-up"></i> <i class="el-icon-arrow-down"></i></span>
              </template>
            </vue-tel-input>
            <!-- <el-input class="input-space" v-model="form.mobile" placeholder="Type here..." /> -->
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <div>Birthdate<span class="required">*</span></div>
          <el-form-item prop="birthdate">
            <el-date-input class="input-space" v-model="form.birthdate" placeholder="DD/MM/YYYY"/>
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <el-input-title required>Country</el-input-title>
          <el-form-item prop="billingAddress.country">
            <el-autocomplete
              class="mt-05 w-100"
              v-model="form.country"
              :fetch-suggestions="searchCountry"
              placeholder="Select"
            ></el-autocomplete>
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <div>Gender<span class="required">*</span></div>
          <el-form-item prop="gender">
            <el-select class="input-space w-100" v-model="form.gender" placeholder="Select">
              <el-option
                v-for="item in genderOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <!-- <el-col :span="24" class="term">
          <el-checkbox v-model="form.acceptPromotion"></el-checkbox>
          Accept promotion
        </el-col> -->
        <el-col :span="24" class="term">
          <el-checkbox v-model="form.acceptTerm"></el-checkbox>
          I agree to the <a href="https://assemble-sports-assets.s3-ap-southeast-2.amazonaws.com/documents/Assemble+Terms+and+Conditions.pdf" target="_blank">Terms and Conditions</a>
        </el-col>
        <el-col :span="24" v-if="errorMessage" class="row-space">
          <el-alert show-icon type="error" :title="errorMessage" :closable="false" />
        </el-col>
      </el-row>
      <el-row class="row-space">
        <el-col :span="24">
          <el-button
            type="primary"
            class="ac-btn"
            @click="complete"
          >Complete</el-button>
        </el-col>
      </el-row>
    </el-form>
  </account-layout>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';
import { VueTelInput } from 'vue-tel-input';
import AccountLayout from './AccountLayout.vue';
import { countries } from '../../utils/constants';

const PasswordValidator = require('password-validator');
const { parsePhoneNumberFromString } = require('libphonenumber-js/max');

const passwordSchema = new PasswordValidator();

export default {
  name: 'AccountCompleteProfile',
  components: {
    AccountLayout,
    VueTelInput
  },
  props: {
    platform: String
  },
  data() {
    const checkEmail = (rule, value, callback) => {
      if (!value) {
        return callback(new Error('Email is required'));
      }
      const re = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
      if (re.test(String(value).toLowerCase())) return callback();
      return callback(new Error('Invalid email address'));
    };
    const checkPassword = (rule, value, callback) => {
      if (!this.showPasswordField) return callback();
      if (!value) {
        return callback(new Error('Password is required'));
      }
      if (+process.env.VUE_APP_NATIONAL_ID === 19 || (+process.env.VUE_APP_NATIONAL_ID === 1 && process.env.VUE_APP_PIN_ENV === 'test')) {
        passwordSchema
          .is().min(8) // Minimum length 8
          .has().lowercase() // Must have lowercase letters
          .has()
          .symbols() // Must have symbols
          .has()
          .not()
          .spaces(); // Should not have spaces
        if (passwordSchema.validate(value)) return callback();
        return callback(new Error('Password must be at least 8 characters and special characters.'));
      }
      passwordSchema
        .is().min(8)
        .has().uppercase()
        .has()
        .lowercase()
        .has()
        .symbols()
        .has()
        .not()
        .spaces();
      if (passwordSchema.validate(value)) return callback();
      return callback(new Error('Password must be at least 8 characters with uppercase, lowercase and special characters.'));
    };
    const checkMobile = (rule, value, callback) => {
      if (!value) {
        return callback(new Error('Mobile is required'));
      }
      // join value remomve all space
      // eslint-disable-next-line no-param-reassign
      value = value.replace(/\s/g, '');
      const phoneNumber = parsePhoneNumberFromString(value, this.countryCode);
      const test = !!(((phoneNumber && phoneNumber.isValid() && (phoneNumber.getType() === 'MOBILE' || phoneNumber.getType() === 'FIXED_LINE_OR_MOBILE'))));
      if (test) return callback();
      return callback(new Error('Invalid mobile'));
    };
    const checkBirthdate = (rule, value, callback) => {
      if (!value) {
        return callback(new Error('Birthdate is required'));
      }
      const re = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;
      if (re.test(String(value).toLowerCase())) return callback();
      return callback(new Error('Invalid birth date'));
    };

    return {
      countries,
      countryDialCode: this.getCountryDialCode(),
      countryCode: this.getCountryCode(),
      errorMessage: '',
      form: {
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        mobile: '',
        birthdate: '',
        gender: '',
        acceptPromotion: false,
        acceptTerm: false
      },
      genderOptions: [
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' },
        { value: 'Other', label: 'Other' }
      ],
      showPasswordField: (+process.env.VUE_APP_NATIONAL_ID === 20),
      signUpRules: {
        email: [
          { validator: checkEmail, trigger: 'blur' }
        ],
        password: [
          { validator: checkPassword, trigger: 'blur' }
        ],
        firstName: [
          { required: true, message: 'First Name is required', trigger: 'blur' }
        ],
        lastName: [
          { required: true, message: 'Last Name is required', trigger: 'blur' }
        ],
        mobile: [
          { validator: checkMobile, trigger: 'blur' }
        ],
        birthdate: [
          { validator: checkBirthdate, trigger: 'blur' }
        ],
        gender: [
          { required: true, message: 'Gender is required', trigger: 'blur' }
        ]
      }
    };
  },
  mounted() {
    this.form = _.merge(this.form, _.pick(this.user, [
      'email', 'firstName', 'lastName'
    ]));
  },
  computed: {
    user() {
      return this.$store.state.user.profile;
    }
  },
  methods: {
    getCountryDialCode() {
      const nationalId = +process.env.VUE_APP_NATIONAL_ID;
      switch (nationalId) {
        case 17:
          return '64'; // New Zealand
        case 21:
          return '65'; // Singapore
        default:
          return '61'; // Australia (default)
      }
    },

    getCountryCode() {
      const nationalId = +process.env.VUE_APP_NATIONAL_ID;
      switch (nationalId) {
        case 17:
          return 'NZ'; // New Zealand
        case 21:
          return 'SG'; // Singapore
        default:
          return 'AU'; // Australia (default)
      }
    },
    handleCountryChanged(countryCode) {
      this.countryDialCode = countryCode.dialCode;
      this.countryCode = countryCode.iso2.toUpperCase();
    },
    searchCountry(query, cb) {
      if (query && query !== '') {
        setTimeout(() => {
          cb(this.countries.filter((item) => item.label.toLowerCase()
            .indexOf(query.toLowerCase()) > -1));
        }, 200);
      } else {
        cb([]);
      }
    },
    complete() {
      this.errorMessage = '';
      this.$refs.form.validate((valid) => {
        if (valid) {
          if (this.form.acceptTerm === false) this.errorMessage = 'Please accept terms before continue';
          else {
            let mobile = _.cloneDeep(this.form.mobile).replace(/\s/g, '');
            if (mobile.charAt(0) === '0') {
              mobile = mobile.substring(1);
              this.form.mobile = `+${ this.countryDialCode }${mobile}`;
            } else if (mobile.charAt(0) === '+') {
              this.form.mobile = `${mobile}`;
            } else {
              this.form.mobile = `+${ this.countryDialCode }${mobile}`;
            }
            const body = {
              email: this.form.email,
              password: this.showPasswordField ? this.form.password : '',
              firstName: this.form.firstName,
              lastName: this.form.lastName,
              dob: moment(this.form.birthdate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
              mobile: this.form.mobile,
              gender: this.form.gender,
              acceptPromotion: this.form.acceptPromotion,
            };
            this.$store.commit('loading', { loading: true, text: 'Loading...' });
            this.$http.post('/assemble/api/v1/account/portal/users/complete-profile', body).then(() => {
              this.$store.commit('user/complete', {
                dob: body.dob,
                mobile: body.mobile,
                gender: body.gender
              });

              this.$message.success('Thanks for completing your profile you can now register for events');
              this.$router.push('/');
              this.$store.commit('loading', false);
            }).catch((e) => {
              this.errorMessage = e.response.data.message;
              this.$store.commit('loading', false);
            });
          }
        }
      });
    },
    login() {
      this.$router.push('login');
    }
  }
};
</script>

<style scoped lang="scss">
.content-card {
  margin-left: 1em;
  margin-right: 1em;
  margin-top: 2em;
  width: 90%;
  max-width: 30em;
}

.su-title {
  text-align: center;
  font-size: 1.5em;
}

.t-right {
  text-align: right;
}

.t-center {
  text-align: center;
}

.required {
  color: $highlight;
  margin-left: 0.2em;
}

.term {
  font-size: 0.8em;
}

.row-space {
  margin-top: 1em;
}

.input-space {
  margin-top: 0.5em;
}

.ac-btn {
  width: 100%;
  background-color: $highlight !important;
  border: 0;
}

.width-100 {
  width: 100%;
}
</style>
